<template>
  <div>
    <div class="video-container">
      <bg-video-player
        :video="data.video"
        :getRef="setVideoRef"
        :currentTime="currentTime"
        :timeUpdate="timeUpdate"
        :newScrubberTime="newScrubberTime"
        :endScrubberTime="endScrubberTime"
        :updateMetaData="handleVideoLoad"
        :customControls="customControls"
      />
    </div>
    <div class="bold select-custom-timeframe">
      Select your custom timeframe:
    </div>
    <div class="chart-container">
      <bg-time-scrubber
        v-if="videoRef"
        :currentTime="currentTime"
        :videoRef="videoRef"
        @handleMovement="handleMovement"
        @handleSizeChange="handleSizeChange"
      />
    </div>
    <div class="selected-scene">
      <span class="bold">Selection:</span> {{ displayStartTime }} -
      {{ displayEndTime }}
    </div>
    <div class="wobble-chart-title">
      <span><strong>Scores</strong> for selected scene</span>
      <div class="wobble-chart-title__tooltip">
        Compare the scores of your custom timeframe to the average, minimum and
        maximum scores of the total video.
      </div>
    </div>
    <div class="wobble-chart-contianer">
      <bg-wobble-bar-chart class="wobble-src" :chartData="chartData" />
    </div>
    <div class="feedback-container" v-if="feedbackList.length > 0">
      <h2 v-bg-typography="'h2'" :class="['feedback-container__title']">
        Neurofeedback
        <span class="feedback-container__scene-title">for selected scene</span>
      </h2>
      <bg-detail-card
        v-for="feedback in feedbackList"
        v-bind:key="feedback.id"
        v-bind="{ ...feedback }"
        :text="feedback.comment"
        :detail="feedback.emotion"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "./styles.scss";
</style>

<script>
import moment from "moment";
import BgVideoPlayer from "./bg-video-player";
import BgTimeScrubber from "./bg-time-scrubber";
import BgDetailCard from "@/components/bg-detail-card";
import BgWobbleBarChart from "@/components/bg-wobble-bar-chart";
import BgLegend from "../bg-legend";
import BgTypography from "@/directives/bg-typography";
export default {
  name: "bg-video-chart",
  components: {
    BgVideoPlayer,
    BgLegend,
    BgTimeScrubber,
    BgDetailCard,
    BgWobbleBarChart,
  },
  directives: {
    BgTypography,
  },
  props: {
    data: Object,
    customControls: false,
  },
  computed: {
    chartData() {
      return {
        labels: ["Frustration", "Joy", "Attention", "Spread", "Eye movements"],
        datasets: [
          {
            data: [
              this.chartFrustration,
              this.chartJoy,
              this.chartAttention,
              this.chartSpread,
              this.chartFluency,
            ],
            barPercentage: 1,
            backgroundColor: [
              "#ff00a9",
              "#00deab",
              "#ffaa00",
              "#EBEBEF",
              "#B1B1C1",
            ],
          },
        ],
      };
    },
    displayStartTime() {
      return this.convertToMMss(this.startTime);
    },
    displayEndTime() {
      return this.convertToMMss(this.endTime);
    },
  },
  created() {
    let joys = [];
    let attentions = [];
    let frustrations = [];
    let spreads = [];
    let fluencies = [];

    this.data.emotions.forEach((emotion) => {
      joys.push(emotion.joy);
      attentions.push(emotion.attention);
      frustrations.push(emotion.frustration);
    });

    this.data.fluency_metrics.forEach((metric) => {
      fluencies.push(metric.value);
    });

    this.data.spread_metrics.forEach((metric) => {
      spreads.push(metric.value);
    });

    this.joyLimits = [Math.max(...joys), Math.min(...joys)];
    this.attentionLimits = [Math.max(...attentions), Math.min(...attentions)];
    this.frustrationLimits = [
      Math.max(...frustrations),
      Math.min(...frustrations),
    ];
    this.fluencyLimits = [Math.max(...fluencies), Math.min(...fluencies)];
    this.spreadLimits = [Math.max(...spreads), Math.min(...spreads)];
  },
  methods: {
    convertToMMss(newTime) {
      const s = parseInt(newTime % 60);
      const m = parseInt((newTime / 60) % 60);
      const time = moment()
        .seconds(s)
        .minutes(m)
        .format("mm:ss");
      return time;
    },
    setVideoRef(ref) {
      this.videoRef = ref;
    },
    timeUpdate(time) {
      this.currentTime = time;
    },
    handleVideoLoad(video) {
      this.handleMovement(0, 15);
      this.totalTime = video.duration;
    },
    handleMovement(leftPercentage, width) {
      const newTime = (leftPercentage / 100) * this.videoRef.duration;
      this.calculateEndTime(leftPercentage + width);
      this.startTime = newTime;
      this.newScrubberTime = newTime;
      this.calculateCustomScene();
      this.handleSizeChange(this.bubbleSize);
    },
    handleSizeChange(size) {
      this.bubbleSize = size;
      const leftPercentage = (100 * this.startTime) / this.videoRef.duration;
      const totalPercentage = size * 100 + leftPercentage;
      this.calculateEndTime(totalPercentage);
      this.endScrubberTime = this.endTime;
      this.calculateCustomScene();
    },
    calculateEndTime(width) {
      const newEndTime = (width / 100) * this.videoRef.duration;
      this.endTime =
        newEndTime > this.videoRef.duration
          ? this.videoRef.duration
          : newEndTime;
    },
    calculateCustomScene() {
      const startTimeStamp = this.convertToUnixTime(this.startTime);
      const endTimeStamp = this.convertToUnixTime(this.endTime);
      const filteredFbs = (this.data.significant_points || [])
        .filter((sp) => sp.comment !== null)
        .filter((sp) => {
          if (
            endTimeStamp > sp.timestamp_top &&
            sp.timestamp_top > startTimeStamp
          ) {
            return sp;
          }
        });
      this.feedbackList = filteredFbs;

      const slicedData = this.data.emotions.filter((e) => {
        if (endTimeStamp > e.timestamp && e.timestamp > startTimeStamp) {
          return e;
        }
      });

      const slicedFluency = this.data.fluency_metrics.filter((f) => {
        if (endTimeStamp > f.timestamp && f.timestamp > startTimeStamp) {
          return f;
        }
      });

      const slicedSpread = this.data.spread_metrics.filter((f) => {
        if (endTimeStamp > f.timestamp && f.timestamp > startTimeStamp) {
          return f;
        }
      });

      this.chartFluency = this.calculateChartValue(slicedFluency, "fluency");
      this.chartSpread = this.calculateChartValue(slicedSpread, "spread");
      this.chartJoy = this.calculateChartValue(slicedData, "joy");
      this.chartFrustration = this.calculateChartValue(
        slicedData,
        "frustration"
      );
      this.chartAttention = this.calculateChartValue(slicedData, "attention");
    },
    convertToUnixTime(time) {
      const timeStamp = moment(this.data.started_at).add(time, "seconds");
      return timeStamp;
    },
    calculateChartValue(data, metricType) {
      const valueFiled = ["fluency", "spread"].includes(metricType)
        ? "value"
        : metricType;
      const avgValue =
        data.reduce((p, c) => p + c[valueFiled], 0) / data.length;

      if (avgValue > 0) {
        return avgValue / this[`${metricType}Limits`][0];
      } else {
        return -(avgValue / this[`${metricType}Limits`][1]);
      }
    },
  },
  data() {
    return {
      videoRef: null,
      totalTime: 0,
      currentTime: 0,
      newScrubberTime: 0,
      endScrubberTime: 0,
      bubbleSize: 0.15,
      startTime: 0,
      endTime: 0,
      feedbackList: [],
      slicedData: this.data.emotions,
      chartJoy: 0,
      chartAttention: 0,
      chartFrustration: 0,
      chartFluency: 0,
      chartSpread: 0,
      joyLimits: [0, 0],
      attentionLimits: [0, 0],
      frustrationLimits: [0, 0],
      fluencyLimits: [0, 0],
      spreadLimits: [0, 0],
    };
  },
};
</script>
